import {
  fetchListDepositRequestPazirande,
  fetchListDepositWalletPazirande,
  fetchListWithdrawRequestPazirande,
} from "@/api/merchant/request.post";
import {
  numberSeparator,
  toPersianNumber,
} from "@/hooks/useThousandsSeparator";
import AdapterMomentJalali from "@/shared/components/common/datePeacker";
import CustomSelect from "@/shared/components/common/select";
import CustomTable from "@/shared/components/common/table";
import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import {Grid,TextField,} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import moment from "jalali-moment";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

/**
 * Content
 *
 * کامپوننت اصلی نمایش لیست تراکنش‌ها و گزارش‌های مالی پذیرنده.
 *
 * ویژگی‌ها و عملکرد:
 *  - نمایش تب‌های "روزانه"، "هفتگی" و "ماهانه" برای فیلتر داده‌ها.
 *  - دریافت داده‌ها از API با استفاده از `react-query` (هوک useMutation).
 *  - امکان جستجو و فیلتر تراکنش‌ها بر اساس تاریخ، نوع و مقدار.
 *  - استفاده از DatePicker شمسی با کتابخانه Moment Jalali.
 *  - پشتیبانی از دکمه دانلود خروجی CSV از داده‌های موجود در جدول.
 *  - کاملاً واکنش‌گرا (responsive) و سازگار با اندازه‌های مختلف صفحه.
 *
 * ساختار کلی:
 *  - بخش کنترل‌ها (انتخاب نوع، تب‌ها، فیلتر تاریخ و جستجو)
 *  - جدول نمایش داده‌ها (CustomTable)
 *  - دکمه دریافت خروجی CSV
 *
 * کامپوننت‌های مورد استفاده:
 *  - AdapterMomentJalali → انتخاب‌گر تاریخ شمسی
 *  - CustomSelect → منوی کشویی سفارشی برای انتخاب تعداد رکورد
 *  - CustomTable → جدول سفارشی برای نمایش داده‌ها
 *  - CustomButton → دکمه‌های سفارشی با استایل اختصاصی
 *  - StackKit / BoxKit / TypoGraphyKit → کامپوننت‌های UI سطح پایه برای چیدمان و تایپوگرافی
 *
 * APIهای مورد استفاده:
 *  - fetchListDepositRequestPazirande → دریافت لیست تراکنش‌ها
 *  - fetchListDepositWalletPazirande
 *  - fetchListWithdrawRequestPazirande
 *
 * @returns JSX.Element — رابط کاربری لیست تراکنش‌ها با قابلیت فیلتر، جستجو و دانلود CSV
 */

export const Content = () => {
  const tabs = ["روزانه", "هفتگی", "ماهانه"];
  const [activeTab, setActiveTab] = useState("روزانه");
  const [dateValue, setDateValue] = useState<Date | string | null>(null);
  const [depositWallet, setDepositWallet] = useState<any>();
  const [searchValue, setSearchValue] = useState("");
  const [value, setValue] = useState(5);

  const { mutate: submitFormDepositRequestPazirande } = useMutation({
    mutationFn: fetchListDepositRequestPazirande,
    onSuccess: (data: any) => {
      setDepositWallet(data?.data);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطا در ثبت اطلاعات");
    },
  });
  const handleChange = (val: any) => {
    setValue(val);
  };

  useEffect(() => {
    const requestBody = {
      date: dateValue ? moment(dateValue, "jYYYY/jMM/jDD").format("YYYY-MM-DD") : moment(new Date()).format("YYYY-MM-DD"),
      search: searchValue,
      type: "monthly",
      count: "5",
    };
    submitFormDepositRequestPazirande(requestBody);
  }, []);

  const handleSearch = () => {
    const requestBody = {
      date:
        dateValue && moment(dateValue, "jYYYY/jMM/jDD").format("YYYY-MM-DD"),
      search: searchValue,
      type: "monthly",
      count: "5",
    };
    submitFormDepositRequestPazirande(requestBody);
  };

  const dataHeader = [
    { id: "id", title: "ردیف" },
    { id: "price", title: "مبلغ برداشت (ریال)" },
    { id: "created_at", title: "ساعت و تاریخ درخواست" },
    { id: "source_card", title: "مبدا تراکنش" },
    { id: "target_card", title: "مقصد تراکنش" },
    { id: "status", title: "وضعیت درخواست" },
    { id: "detaile", title: "توضیحات" },
  ];

  const newData = depositWallet?.withdraws.map((item: any, index: number) => {
    return {
      rowId: index + 1,
      source_card: (
        <TypoGraphyKit sx={{ direction: "ltr" }}>
          {toPersianNumber(item.source_card)}
        </TypoGraphyKit>
      ),
      status: item.status ? item.status : "-",
      target_card: (
        <TypoGraphyKit sx={{ direction: "ltr" }}>
          {toPersianNumber(item.target_card)}
        </TypoGraphyKit>
      ),

      created_at: toPersianNumber(
        moment(item.created_at).locale("fa").format("jYYYY/jMM/jDD")
      ),
      price: numberSeparator(item.price),
    };
  });
  const handleDownloadCSV = () => {
    if (!depositWallet?.withdraws || depositWallet.withdraws.length === 0)
      return;

    const headers = [
      "ردیف",
      "مبلغ برداشت",
      "ساعت و تاریخ درخواست",
      "مبدا تراکنش",
      "مقصد تراکنش",
      "وضعیت درخواست",
      "توضیحات",
    ];

    const rows = depositWallet.withdraws.map((item: any, index: number) => [
      index + 1,
      item.price,
      moment(item.created_at).locale("fa").format("jYYYY/jMM/jDD HH:mm"),
      item.source_card,
      item.target_card,
      item.status,
      item.detaile,
    ]);

    const csvContent = [headers, ...rows].map((e) => e.map((v: any) => `"${v ?? ""}"`).join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "transactions.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fakeData = newData;

  return (
    <StackKit sx={{ width: "100%" }} gap={3}>
      <Grid container spacing={2}>
        {/* Filters & Tabs Section */}
        <Grid size={{xs:12}}>
          <StackKit direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} gap={2} sx={{ flexWrap: "wrap" }}>
            {/* Left Controls (Select + Tabs) */}
            <StackKit direction={{ xs: "column", sm: "row" }} gap={2} alignItems="center" flexWrap="wrap" sx={{ width: { xs: "100%", md: "auto" } }}>
              <CustomSelect
                label={
                  <TypoGraphyKit variant="body1" sx={{ color: "#A4A4A4" }}>
                    تراکنش آخر
                  </TypoGraphyKit>
                } options={[5, 10, 15, 20]} onChange={handleChange} value={value || 10}
              />

              <StackKit direction="row" flexWrap="wrap" justifyContent="center" alignItems="center" sx={{ bgcolor: "#5A5A5A", p: 1.2, borderRadius: 2, width: { xs: "100%", sm: "auto" }, overflowX: "auto", "& > :not(style) ~ :not(style)": { marginLeft: "0 !important", }}}>
                {tabs.map((tab) => (
                  <StackKit
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                      width:{xs:'100%',sm:'unset'},
                      bgcolor: activeTab === tab ? "#434343" : "transparent",
                      color: activeTab === tab ? "#fff" : "#B3B3B3",
                      border: activeTab === tab ? "1px solid #434343" : "1px solid transparent",
                      "&:hover": {
                        bgcolor: activeTab === tab ? "#434343" : "#666666",
                      },
                    }}
                  >
                    <TypoGraphyKit variant="body2" sx={{ color: "inherit" }}>
                      {tab}
                    </TypoGraphyKit>
                  </StackKit>
                ))}
              </StackKit>
            </StackKit>

            {/* Right Controls (DatePicker + Search) */}
            <StackKit direction={{ xs: "column", md: "row" }} alignItems="center" justifyContent="space-between" gap={2} >
              <AdapterMomentJalali heightProps="45px" valueDate={dateValue ? moment(dateValue, "jYYYY/jMM/jDD") : null}
                handleChange={(newValue: any) => {
                  const formatted = newValue?.format("jYYYY-jMM-jDD");
                  setDateValue(formatted);
                }}
              />

              <TextField placeholder="وارد کردن جزئیات جستجو" fullWidth value={searchValue} onChange={(e) => setSearchValue(e.target.value)} 
                sx={{flex: 1,width: "100%","& .MuiInputBase-root": {height: "45px",},}}/>
              <CustomButton onClick={handleSearch} sx={{ px: 3, height: "45px", whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" },}}>
                <TypoGraphyKit variant="body1">جستجو</TypoGraphyKit>
              </CustomButton>
            </StackKit>
          </StackKit>
        </Grid>

        {/* Table Section */}
        <Grid size={{xs:12}}>
          <BoxKit sx={{ overflowX: "auto" }}>
            <CustomTable dataHeader={dataHeader} dataTbody={fakeData} />
          </BoxKit>
        </Grid>

        {/* Download Button */}
        <Grid size={{xs:12}}>
          <StackKit direction="row" justifyContent={{ xs: "center", md: "flex-end" }} alignItems="center">
            <CustomButton sx={{ bgcolor: "#5A5A5A", py: 1.5, px: 3, borderRadius: 2, width: { xs: "100%", sm: "auto" }, }} onClick={handleDownloadCSV}>
              <TypoGraphyKit variant="body1">
                دریافت صورتحساب به صورت فایل
              </TypoGraphyKit>
            </CustomButton>
          </StackKit>
        </Grid>
      </Grid>
    </StackKit>

  );
};
